import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useActionsPortefeuille, useRecouvrements } from '@/api/hooks';
import { LIBELLE_ETAPE_RECOUVREMENT, TYPES_ETAPE_RECOUVREMENT, type RecouvrementVue, type TypeEtapeRecouvrement } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { montantDevise } from '@/components/rh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Textarea } from '@/components/ui/textarea';
import { dateFr, dateHeureFr } from '@/lib/utils';

const LIBELLE_STATUT = { OUVERT: 'Ouvert', EN_COURS: 'En cours', CLOS: 'Clos' } as const;
const usd = (n: number) => montantDevise(n, 'USD');

/** Recouvrement (droit de suite, point 31) : créances exigibles au départ d'un agent, étapes, encaissements, clôture par le Directeur Général. */
export function Recouvrement() {
  const u = useSession()!.utilisateur;
  const dg = u.profils.includes('DIRECTEUR_GENERAL');
  const peutAgir = u.profils.some((p) => ['INVESTISSEMENTS', 'DIRECTEUR_FINANCIER', 'DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'DIRECTEUR_GENERAL'].includes(p));
  const { data, isLoading, error } = useRecouvrements();
  const actions = useActionsPortefeuille();
  const [etape, setEtape] = useState<RecouvrementVue | null>(null);
  const [cloture, setCloture] = useState<RecouvrementVue | null>(null);
  if (isLoading) return <Chargement />;
  if (error) return <ErreurBloc erreur={error} />;
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setEtape(null);
      setCloture(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };
  return (
    <div>
      <PageHeader titre="Recouvrement des prêts sociaux" sousTitre="Créances exigibles au départ d'un agent : imputation sur le décompte final, reliquat, mises en demeure, encaissements, clôture par encaissement ou par décision motivée du Directeur Général." />
      {!data?.length ? (
        <Vide message="Aucun dossier de recouvrement." />
      ) : (
        <div className="space-y-4">
          {data.map((r) => (
            <Card key={r.id} data-testid="recouvrement">
              <CardHeader>
                <CardTitle className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex flex-wrap items-center gap-2">
                    <Link to={`/rh/portefeuille/${r.pretId}`} className="text-primary hover:underline">
                      {r.numero}
                    </Link>
                    · {r.agent.nom} <Badge variant={r.statut === 'CLOS' ? 'outline' : 'destructive'}>{LIBELLE_STATUT[r.statut]}</Badge>
                  </span>
                  <span className="flex gap-2">
                    {peutAgir && r.statut !== 'CLOS' && (
                      <Button size="sm" variant="outline" onClick={() => setEtape(r)}>
                        Nouvelle étape
                      </Button>
                    )}
                    {dg && r.statut !== 'CLOS' && (
                      <Button size="sm" onClick={() => setCloture(r)}>
                        Clôturer (décision motivée)
                      </Button>
                    )}
                  </span>
                </CardTitle>
                <CardDescription>
                  Exigible depuis le {dateFr(r.dateExigibilite)} ({r.anciennete} jours) · {r.motif} · exigible {usd(r.montantExigible)}, imputé sur le décompte final {usd(r.imputeDecompte)}, reliquat {usd(r.reliquat)}, encaissé {usd(r.encaisse)}, <span className="font-medium text-foreground">restant {usd(r.restant)}</span>
                  {r.decisionCloture ? ` · clôture : ${r.decisionCloture}${r.closLe ? ` (${dateHeureFr(r.closLe)})` : ''}` : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  {r.etapes.map((e, i) => (
                    <li key={i}>
                      <span className="text-muted-foreground">{dateFr(e.date)}</span> · <span className="font-medium">{LIBELLE_ETAPE_RECOUVREMENT[e.type]}</span>
                      {e.montant ? ` · ${usd(e.montant)}` : ''} · {e.commentaire} <span className="text-muted-foreground">({e.parNom})</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {etape && <EtapeDialog r={etape} onClose={() => setEtape(null)} onConfirmer={(corps) => executer(() => actions.etapeRecouvrement.mutateAsync({ id: etape.id, corps }), 'Étape enregistrée.')} chargement={actions.etapeRecouvrement.isPending} />}
      {cloture && <ClotureDialog r={cloture} onClose={() => setCloture(null)} onConfirmer={(decision) => executer(() => actions.cloturerRecouvrement.mutateAsync({ id: cloture.id, decision }), 'Dossier clos.')} chargement={actions.cloturerRecouvrement.isPending} />}
    </div>
  );
}

function EtapeDialog({ r, onClose, onConfirmer, chargement }: { r: RecouvrementVue; onClose: () => void; onConfirmer: (corps: { type: string; commentaire: string; montant?: number; date?: string; reference?: string }) => void; chargement: boolean }) {
  const [type, setType] = useState<TypeEtapeRecouvrement>('MISE_EN_DEMEURE');
  const [commentaire, setCommentaire] = useState('');
  const [montant, setMontant] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Étape du recouvrement · {r.numero}</DialogTitle>
          <DialogDescription>Restant à recouvrer : {usd(r.restant)}. Un encaissement complet clôt le dossier.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <SelectNatif value={type} onChange={(e) => setType(e.target.value as TypeEtapeRecouvrement)}>
              {TYPES_ETAPE_RECOUVREMENT.map((t) => (
                <option key={t} value={t}>
                  {LIBELLE_ETAPE_RECOUVREMENT[t]}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rec-date">Date</Label>
              <Input id="rec-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            {type === 'ENCAISSEMENT' && (
              <div className="space-y-1.5">
                <Label htmlFor="rec-montant">Montant encaissé</Label>
                <Input id="rec-montant" type="number" min={0.01} step="0.01" value={montant} onChange={(e) => setMontant(e.target.value)} />
              </div>
            )}
          </div>
          {type === 'ENCAISSEMENT' && (
            <div className="space-y-1.5">
              <Label htmlFor="rec-ref">Référence</Label>
              <Input id="rec-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="rec-com">Commentaire</Label>
            <Textarea id="rec-com" rows={3} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer({ type, commentaire: commentaire.trim(), montant: type === 'ENCAISSEMENT' ? Number(montant) : undefined, date, reference: reference || undefined })} disabled={commentaire.trim().length < 3 || (type === 'ENCAISSEMENT' && !(Number(montant) > 0)) || chargement}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClotureDialog({ r, onClose, onConfirmer, chargement }: { r: RecouvrementVue; onClose: () => void; onConfirmer: (decision: string) => void; chargement: boolean }) {
  const [decision, setDecision] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Clôture par décision motivée · {r.numero}</DialogTitle>
          <DialogDescription>Restant non recouvré : {usd(r.restant)}. La décision du Directeur Général est journalisée ; le prêt est soldé.</DialogDescription>
        </DialogHeader>
        <Textarea rows={4} placeholder="Décision motivée (10 caractères au moins)" value={decision} onChange={(e) => setDecision(e.target.value)} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer(decision.trim())} disabled={decision.trim().length < 10 || chargement}>
            Clôturer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
